import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { History, Square } from "lucide-react";
import {
  type AggSource,
  type ChannelDetail,
  FILL_LIMITS,
  type Fill,
  type FillMode,
  type FillSource,
  sourceLabel,
  useStartFill,
  useStopFill,
} from "@/lib/aggregator";
import { formatMoment, formatNumber } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Choice } from "@/components/ownchats/CreateOwnChatDialog";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProgressBar } from "@/components/ui/progress";
import { errorText } from "./helpers";
import { Section } from "./parts";

/** A span of minutes as a person says it: "40 мин", "3 ч 20 мин", "2 сут 4 ч". */
function spanText(t: TFunction, minutes: number): string {
  const total = Math.max(1, Math.round(minutes));
  if (total < 60) return t("aggregator.fill.span_m", { m: total });
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours < 24) {
    return rest
      ? t("aggregator.fill.span_hm", { h: hours, m: rest })
      : t("aggregator.fill.span_h", { h: hours });
  }
  const days = Math.floor(hours / 24);
  return hours % 24
    ? t("aggregator.fill.span_dh", { d: days, h: hours % 24 })
    : t("aggregator.fill.span_d", { d: days });
}

function whole(value: string): number {
  return /^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN;
}

/**
 * Fill the channel with its sources' older posts, so that a new channel does
 * not stand empty: the latest posts of each source, those of a period, or the
 * most viewed of them. They go through the channel's settings and out one at
 * a time, oldest first, a random gap apart.
 */
export function FillDialog({
  channel,
  open,
  onOpenChange,
  only,
}: {
  channel: ChannelDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The sources to start with ticked; all of the channel's when not given. */
  only?: string[] | null;
}) {
  const { t } = useTranslation();
  const start = useStartFill();
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<FillMode>("last");
  const [count, setCount] = useState("20");
  const [days, setDays] = useState("7");
  const [gapMin, setGapMin] = useState("10");
  const [gapMax, setGapMax] = useState("30");
  const [skipTaken, setSkipTaken] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Every opening starts clean, with the sources it was opened for.
  useEffect(() => {
    if (!open) return;
    // A source on pause is not read by a fill: not ticked unless asked for.
    const ids = channel.source_list
      .filter((source) => source.status === "active" || only?.includes(source.id))
      .map((source) => source.id);
    setPicked(only && only.length > 0 ? ids.filter((id) => only.includes(id)) : ids);
    setMode("last");
    setCount("20");
    setDays("7");
    setGapMin("10");
    setGapMax("30");
    setSkipTaken(true);
    setError(null);
    // The card's sources may change under an open dialog; the ticks may not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sources = channel.source_list;
  const chosen = sources.filter((source) => picked.includes(source.id));
  const n = chosen.length;
  const wantsCount = mode !== "days";
  const wantsDays = mode !== "last";
  const posts = whole(count);
  const period = whole(days);
  const shortest = whole(gapMin);
  const longest = whole(gapMax);
  const perSource = Math.floor(FILL_LIMITS.posts / Math.max(1, n));

  const problem =
    n === 0
      ? t("aggregator.fill.error_sources")
      : wantsCount && !(posts >= 1)
        ? t("aggregator.fill.error_count")
        : wantsCount && posts * n > FILL_LIMITS.posts
          ? t("aggregator.fill.error_too_many", {
              max: FILL_LIMITS.posts,
              sources: n,
              per: perSource,
            })
          : wantsDays && !(period >= 1 && period <= FILL_LIMITS.days)
            ? t("aggregator.fill.error_days", { max: FILL_LIMITS.days })
            : !(shortest >= 1 && longest >= shortest && longest <= FILL_LIMITS.gapMinutes)
              ? t("aggregator.fill.error_gap")
              : null;

  const planned = wantsCount && posts >= 1 ? posts * n : null;
  const gaps = shortest >= 1 && longest >= shortest ? (shortest + longest) / 2 : null;
  const closed = chosen.some((source) => source.via === "account");

  const submit = async () => {
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    try {
      await start.mutateAsync({
        id: channel.id,
        source_ids: chosen.map((source) => source.id),
        mode,
        count: wantsCount ? posts : null,
        days: wantsDays ? period : null,
        gap_min: shortest,
        gap_max: longest,
        skip_taken: skipTaken,
      });
      pushToast({ kind: "success", text: t("aggregator.fill.started_toast") });
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  const toggle = (source: AggSource, on: boolean) =>
    setPicked((now) =>
      on
        ? [...now.filter((id) => id !== source.id), source.id]
        : now.filter((id) => id !== source.id),
    );

  return (
    <Dialog open={open} onOpenChange={(next) => !start.isPending && onOpenChange(next)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("aggregator.fill.dialog_title")}</DialogTitle>
          <DialogDescription>{t("aggregator.fill.dialog_hint")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {channel.status !== "active" ? (
            <Alert tone="info">{t("aggregator.fill.off_note")}</Alert>
          ) : (
            channel.problem && <Alert tone="warning">{t("aggregator.fill.stopped_note")}</Alert>
          )}

          <div className="space-y-2">
            <Label>{t("aggregator.fill.sources")}</Label>
            <ul className="space-y-1.5">
              {sources.map((source) => (
                <li key={source.id}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-fg-primary">
                    <Checkbox
                      checked={picked.includes(source.id)}
                      onCheckedChange={(v) => toggle(source, v === true)}
                    />
                    <span className="truncate">
                      {sourceLabel(source, t("aggregator.sources.closed_name"))}
                    </span>
                    {source.via === "account" && (
                      <span className="shrink-0 text-xs text-fg-muted">
                        {t("aggregator.fill.via_account")}
                      </span>
                    )}
                    {source.status === "paused" && (
                      <span className="shrink-0 text-xs text-warning">
                        {t("aggregator.fill.paused_source")}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2">
            <Label>{t("aggregator.fill.what")}</Label>
            <div
              role="radiogroup"
              aria-label={t("aggregator.fill.what")}
              className="flex flex-col gap-2 sm:flex-row"
            >
              {(["last", "days", "best"] as const).map((option) => (
                <Choice
                  key={option}
                  active={mode === option}
                  onClick={() => setMode(option)}
                  title={t(`aggregator.fill.mode_${option}`)}
                  hint={t(`aggregator.fill.mode_${option}_hint`)}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            {wantsCount && (
              <div className="w-full space-y-1.5 sm:w-72">
                <Label htmlFor="agg_fill_count">
                  {t(mode === "best" ? "aggregator.fill.count_best" : "aggregator.fill.count")}
                </Label>
                <Input
                  id="agg_fill_count"
                  inputMode="numeric"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                />
              </div>
            )}
            {wantsDays && (
              <div className="w-full space-y-1.5 sm:w-auto">
                <Label htmlFor="agg_fill_days">{t("aggregator.fill.days")}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="agg_fill_days"
                    inputMode="numeric"
                    className="w-20"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  />
                  {[1, 3, 7, 30].map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant={period === d ? "default" : "outline"}
                      onClick={() => setDays(String(d))}
                    >
                      {t("aggregator.fill.days_chip", { n: d })}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-fg-primary">
              <Checkbox checked={skipTaken} onCheckedChange={(v) => setSkipTaken(v === true)} />
              {t("aggregator.fill.skip_taken")}
            </label>
            <p className="pl-6 text-xs leading-relaxed text-fg-muted">
              {t(skipTaken ? "aggregator.fill.skip_taken_on" : "aggregator.fill.skip_taken_off")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="agg_fill_gap_min">{t("aggregator.fill.pace")}</Label>
            <div className="flex items-center gap-2 text-sm text-fg-secondary">
              <span>{t("aggregator.fill.pace_from")}</span>
              <Input
                id="agg_fill_gap_min"
                inputMode="numeric"
                className="w-20"
                value={gapMin}
                onChange={(e) => setGapMin(e.target.value)}
              />
              <span>{t("aggregator.fill.pace_to")}</span>
              <Input
                inputMode="numeric"
                id="agg_fill_gap_max"
                aria-label={t("aggregator.fill.pace_to")}
                className="w-20"
                value={gapMax}
                onChange={(e) => setGapMax(e.target.value)}
              />
              <span>{t("aggregator.fill.pace_unit")}</span>
            </div>
            <p className="text-xs leading-relaxed text-fg-muted">
              {t("aggregator.fill.pace_hint")}
            </p>
          </div>

          {!problem && (
            <p className="text-sm text-fg-primary">
              {planned !== null
                ? t("aggregator.fill.plan_posts", { n: formatNumber(planned) })
                : t("aggregator.fill.plan_days", { days: period, cap: perSource })}
              {planned !== null && gaps !== null && (
                <> {t("aggregator.fill.plan_time", { span: spanText(t, planned * gaps) })}</>
              )}
            </p>
          )}
          {channel.ai_mode !== "off" && !problem && (
            <Alert tone="warning">
              {planned !== null
                ? t("aggregator.fill.ai_note", { n: formatNumber(planned) })
                : t("aggregator.fill.ai_note_days")}
            </Alert>
          )}
          {closed && <Alert tone="info">{t("aggregator.fill.closed_note")}</Alert>}
          <ul className="list-disc space-y-1 pl-5 text-xs leading-relaxed text-fg-muted">
            <li>{t("aggregator.fill.repeat_note")}</li>
            <li>{t("aggregator.fill.fresh_note")}</li>
          </ul>
          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" disabled={start.isPending} onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={start.isPending}
            disabled={start.isPending}
            iconLeft={<History size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("aggregator.fill.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
FillDialog.displayName = "FillDialog";

/** What one source of a fill is at, in words. */
function sourceState(t: TFunction, source: FillSource, running: boolean): string {
  if (!running && ["waiting", "reading", "fetching"].includes(source.state)) {
    return t("aggregator.fill.source_unfinished");
  }
  switch (source.state) {
    case "waiting":
      return source.detail
        ? t("aggregator.fill.source_waiting_why", {
            why: t(`aggregator.problem.${source.detail}`, source.detail),
          })
        : t("aggregator.fill.source_waiting");
    case "reading":
      return t(
        source.via === "account"
          ? "aggregator.fill.source_reading_account"
          : "aggregator.fill.source_reading",
      );
    case "fetching":
      return t("aggregator.fill.source_fetching", {
        n: source.found - source.already - source.taken,
      });
    case "done":
      return [
        t("aggregator.fill.source_done", { taken: source.taken }),
        source.already > 0 && t("aggregator.fill.source_already", { n: source.already }),
        source.skipped > 0 && t("aggregator.fill.source_skipped", { n: source.skipped }),
        source.detail === "cut_short" && t("aggregator.fill.source_cut_short"),
      ]
        .filter(Boolean)
        .join(", ");
    case "failed":
      return source.detail === "paused"
        ? t("aggregator.fill.source_paused")
        : t("aggregator.fill.source_failed", {
            why: t(`aggregator.problem.${source.detail}`, source.detail ?? ""),
          });
    default:
      return t("aggregator.fill.source_gone");
  }
}

/** How a fill is getting on: what is out, what waits, when it ends. */
export function FillProgress({ fill, channel }: { fill: Fill; channel: ChannelDetail }) {
  const { t } = useTranslation();
  const running = fill.status === "running";
  const total = fill.total;
  const reading = fill.sources.some((s) => s.state === "waiting" || s.state === "reading");
  const asked =
    fill.mode === "last"
      ? t("aggregator.fill.asked_last", { count: fill.count ?? 0 })
      : fill.mode === "days"
        ? t("aggregator.fill.asked_days", { days: fill.days ?? 0 })
        : t("aggregator.fill.asked_best", { count: fill.count ?? 0, days: fill.days ?? 0 });
  const numbers = [
    fill.waiting > 0 && t("aggregator.fill.waiting", { n: fill.waiting }),
    fill.review > 0 && t("aggregator.fill.review", { n: fill.review }),
    fill.dropped > 0 && t("aggregator.fill.dropped", { n: fill.dropped }),
    fill.stopped > 0 && t("aggregator.fill.stopped_left", { n: fill.stopped }),
  ].filter(Boolean);

  return (
    <div
      className="space-y-3 rounded-xl border border-border bg-bg-primary px-4 py-3"
      data-fill-status={fill.status}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-fg-primary">
          {t(`aggregator.fill.status_${fill.status}`)}
        </span>
        <span className="text-xs text-fg-muted">
          {asked} · {t("aggregator.fill.asked_pace", { from: fill.gap_min, to: fill.gap_max })}
        </span>
      </div>
      {total > 0 && (
        <ProgressBar
          value={fill.published}
          max={total}
          tone={fill.status === "stopped" ? "neutral" : "accent"}
          label={t("aggregator.fill.progress", {
            published: formatNumber(fill.published),
            total: formatNumber(total),
          })}
        />
      )}
      {numbers.length > 0 && <p className="text-xs text-fg-secondary">{numbers.join(" · ")}</p>}
      {running && channel.status !== "active" && (
        <p className="text-xs text-warning">{t("aggregator.fill.paused")}</p>
      )}
      {running && channel.status === "active" && reading && (
        <p className="text-xs text-fg-muted">{t("aggregator.fill.reading")}</p>
      )}
      {running && fill.ends_at && (
        <p className="text-xs text-fg-muted">
          {t("aggregator.fill.ends_at", { when: formatMoment(fill.ends_at, channel.timezone) })}
        </p>
      )}
      <ul className="space-y-1 text-xs">
        {fill.sources.map((source) => (
          <li key={source.id} className="flex flex-wrap gap-x-2">
            <span className="truncate text-fg-primary">{source.name}</span>
            <span className={source.state === "failed" ? "text-danger" : "text-fg-muted"}>
              {sourceState(t, source, running)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The channel's card block: a fill's progress, and the button for one. */
export function FillSection({ channel, canEdit }: { channel: ChannelDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const stop = useStopFill();
  const [open, setOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const fill = channel.fill;
  const running = fill?.status === "running";

  return (
    <Section
      title={t("aggregator.fill.title")}
      hint={t("aggregator.fill.hint")}
      aside={
        canEdit &&
        (running ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setStopping(true)}
            iconLeft={<Square size={14} strokeWidth={2} aria-hidden="true" />}
          >
            {t("aggregator.fill.stop")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={channel.sources === 0}
            title={channel.sources === 0 ? t("aggregator.fill.no_sources") : undefined}
            onClick={() => setOpen(true)}
            iconLeft={<History size={14} strokeWidth={2} aria-hidden="true" />}
          >
            {t(fill ? "aggregator.fill.again" : "aggregator.fill.start")}
          </Button>
        ))
      }
    >
      {fill && <FillProgress fill={fill} channel={channel} />}
      <FillDialog channel={channel} open={open} onOpenChange={setOpen} />
      <ConfirmDialog
        open={stopping}
        onOpenChange={setStopping}
        tone="danger"
        title={t("aggregator.fill.stop_title")}
        description={t("aggregator.fill.stop_hint")}
        confirmText={t("aggregator.fill.stop")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!fill) return;
          try {
            await stop.mutateAsync({ id: channel.id, fill_id: fill.id });
            pushToast({ kind: "success", text: t("aggregator.fill.stopped_toast") });
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
    </Section>
  );
}
FillSection.displayName = "FillSection";
